import React, { useMemo, useState, useRef, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, A11y, Pagination } from "swiper/modules";
import { Box } from "@mantine/core";

// Import Swiper styles
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import styles from "@scss/custom-components/custom-brand-carousel.module.scss";
import { useMediaQuery } from "@mantine/hooks";

interface CustomBrandCarouselProps {
  images: string[];
  autoplay?: boolean;
  autoplayDelay?: number;
  showPagination?: boolean;
}

export const CustomBrandCarousel: React.FC<CustomBrandCarouselProps> = ({
  images,
  autoplay = true,
  autoplayDelay = 2500,
  showPagination = true,
}) => {
  const isOverMd = useMediaQuery('(min-width: 768px)');
  const swiperRef = useRef<any>(null);

  const hasMultipleBrands = images && images.length > 1;

  const mobileSliderSettings = useMemo(
    () => ({
      modules: [Autoplay, Navigation, A11y, Pagination],
      slidesPerView: "auto" as const,
      spaceBetween: 30,
      loop: true,
      navigation: false,
      centeredSlides: true,
      initialSlide: hasMultipleBrands ? 1 : 0,
      autoplay: autoplay
        ? {
            delay: autoplayDelay,
            disableOnInteraction: false,
          }
        : false,
      pagination: showPagination
        ? {
            el: ".swiper-pagination",
            clickable: true,
          }
        : false,
      breakpoints: {
        768: {
          spaceBetween: 0,    
          slidesPerView: 4,
        },
        992: {
          slidesPerView: 7,
          spaceBetween: 1042,
        },
      },
      observer: true,
      observeParents: true,
      observeSlideChildren: true,
    }),
    [hasMultipleBrands, autoplay, autoplayDelay, showPagination],
  );

  const desktopSliderSettings = useMemo(
    () => ({
      modules: [Autoplay, Navigation, A11y],
      slidesPerView: "auto" as const,
      spaceBetween: 104,
      slidesOffsetBefore: 102,
      slidesOffsetAfter: 102,
      centeredSlides: true,
      initialSlide: 0,
      loop: true,
      autoplay: autoplay
        ? {
            delay: autoplayDelay,
            disableOnInteraction: false,
          }
        : false,
      pagination: showPagination
        ? {
            el: ".swiper-pagination",
            clickable: true,
          }
        : false,
      breakpoints: {
        768: {
          spaceBetween: 0,    
          slidesPerView: 3,
        },
        992: {
          slidesPerView: 7,
          spaceBetween: 1042,
        },
      },
      observer: true,
      observeParents: true,
      observeSlideChildren: true,
    }),
    [autoplay, autoplayDelay, showPagination],
  );

  const [sliderSettings, setSliderSettings] = useState(
    isOverMd ? desktopSliderSettings : mobileSliderSettings,
  );

  useEffect(() => {
    setSliderSettings(isOverMd ? desktopSliderSettings : mobileSliderSettings);
  }, [isOverMd, desktopSliderSettings, mobileSliderSettings]);

  // Attach autoplay on mobile interaction on `touchstart`
  useEffect(() => {
    const handleUserInteraction = () => {
      if (swiperRef.current && swiperRef.current.autoplay) {
        swiperRef.current.autoplay.start();
      }
    };

    // NOTE: workaround for WebKit's need for an initial user interaction
    // for autoplaying elements
    if (!isOverMd) {
      window.addEventListener("touchstart", handleUserInteraction);
    }

    return () => {
      window.removeEventListener("touchstart", handleUserInteraction);
    };
  }, [isOverMd]);

  const carouselClasses = `${styles.carouselContainer}${hasMultipleBrands ? " multi-brands" : " solo-brand"}`;

  return (
    <Box className={carouselClasses}>
      <div className={styles.carouselInner}>
        <Swiper
          ref={swiperRef}
          {...sliderSettings}
          onSwiper={(swiper) => (swiperRef.current = swiper)}
          className={`${styles.swiper} swiper-no-swiping`}
        >
          {images.map((image, index) => (
            <SwiperSlide key={`brand-${index}-slide`} className={styles.swiperSlide}>
              <div className={styles.brandLogo}>
                <img
                  src={image}
                  alt={`Brand ${index + 1}`}
                  className={styles.image}
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </Box>
  );
};
